import dayjs from 'dayjs'

import {
  markJsonArtifact,
  QODER_ARTIFACT_DELIVERED_EVENT,
  QODER_BLOCK_TYPES,
  QODER_ERROR_EVENT,
  QODER_THINKING_EVENT,
  QODER_TOOL_USE_EVENT
} from './use-qoder-cloud-agent'

// 历史事件里用户与 Agent 的类型区分，列表接口与 SSE 事件共用这套类型名。
export const QODER_HISTORY_USER_EVENT = 'user.message'
export const QODER_HISTORY_MESSAGE_EVENT = 'agent.message'

// 单次拉取的历史事件条数。
export const QODER_HISTORY_EVENT_LIMIT = 100

// 投递文件的工具名：artifact_delivered 事件只表示“投递已完成”，文件信息在其入参里。
const DELIVER_ARTIFACTS_TOOL = 'DeliverArtifacts'

// artifact 卡片按 contentType 选择图标，历史事件不带 MIME，按扩展名还原。
const ARTIFACT_CONTENT_TYPE_BY_EXTENSION = Object.freeze({
  html: 'text/html',
  htm: 'text/html',
  md: 'text/markdown',
  txt: 'text/plain',
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
})

const THINKING_TEXT = 'Agent 正在思考'
const DEFAULT_ERROR_TEXT = '请求异常，请稍后重试'
const TIME_FORMAT = 'YYYY-MM-DD HH:mm'
// 工具回显是单行文本，超长内容不与自身展示；Write 的 content 是整份文件，也不应入库。
const TOOL_TEXT_MAX_LENGTH = 200

// 已知工具优先展示有意义的入参，避免把 Write 的整份文件内容塞进消息。
const TOOL_TEXT_FIELD_BY_TOOL = Object.freeze({
  Read: 'file_path',
  Write: 'file_path',
  Edit: 'file_path',
  Bash: 'description',
  Glob: 'pattern',
  Grep: 'pattern',
  WebFetch: 'url',
  WebSearch: 'query',
  Task: 'description'
})

/** 只取字符串值：非字符串一律按空串处理，避免把对象、数字等拼进展示文本。 */
const readString = (value) => (typeof value === 'string' ? value : '')

/**
 * 读取事件字段，同时兼容 camelCase 与 snake_case 的返回。
 * @param {Object} source 事件对象
 * @param {string} camelKey 驼峰字段名
 * @param {string} snakeKey 下划线字段名
 * @returns {*} 字段值
 */
const readField = (source, camelKey, snakeKey) => source?.[camelKey] ?? source?.[snakeKey]

/** 取事件类型。 */
const getEventType = (event) => event?.type || ''

/** 取事件时间，并格式化为可读时间，解析失败时保留原值。 */
const formatProcessedAt = (value) => {
  if (!value) return ''
  const date = dayjs(value)
  return date.isValid() ? date.format(TIME_FORMAT) : String(value)
}

/** 读取事件时间并格式化，两种命名风格都兼容。 */
const readProcessedAt = (source) =>
  formatProcessedAt(readField(source, 'processedAt', 'processed_at'))

export const formatMessageDisplayTime = (value) => {
  if (!value) return ''
  const date = dayjs(value)
  if (!date.isValid()) return ''
  return date.format(date.isSame(dayjs(), 'day') ? 'HH:mm' : 'MM-DD HH:mm')
}

/**
 * 从 content 数组中提取文本内容，兼容字符串与非文本内容项。
 * @param {Object|string} data 事件或其 content 容器
 * @returns {string} 拼接后的文本
 */
const extractText = (data) => {
  if (typeof data === 'string') return data
  const contentList = Array.isArray(data?.content) ? data.content : []
  if (contentList.length) {
    return contentList.map((item) => (typeof item === 'string' ? item : item?.text || '')).join('')
  }
  return typeof data?.text === 'string' ? data.text : ''
}

/** 工具回显是单行文本，过长的入参截断展示。 */
const truncateToolDetail = (detail) =>
  detail.length > TOOL_TEXT_MAX_LENGTH ? `${detail.slice(0, TOOL_TEXT_MAX_LENGTH)}…` : detail

/** 取已知工具优先展示的入参名，未登记的工具返回空串，避免误取原型链上的同名属性。 */
const getToolTextField = (name) =>
  Object.prototype.hasOwnProperty.call(TOOL_TEXT_FIELD_BY_TOOL, name)
    ? TOOL_TEXT_FIELD_BY_TOOL[name]
    : ''

/**
 * 组装工具调用回显文本，格式与 SSE 一致：name: detail。
 * @param {Object} data 工具调用事件
 * @returns {string} 回显文本
 */
const formatToolUseText = (data) => {
  const name = data?.name
  if (!name) return ''

  const input = data?.input
  if (!input || typeof input !== 'object') return name

  const detail = readString(input[getToolTextField(name)]) || readString(input.query)
  if (detail) return truncateToolDetail(detail)

  // 未知工具退化为第一个非空字符串入参，保证不至于丢掉全部上下文。
  const fallback = Object.values(input).find((value) => typeof value === 'string' && value)
  return fallback ? truncateToolDetail(fallback) : name
}

/** 判断事件是否为文件投递工具调用。 */
const isDeliverArtifactsEvent = (event) =>
  getEventType(event) === QODER_TOOL_USE_EVENT && event?.name === DELIVER_ARTIFACTS_TOOL

/** 取文件扩展名的小写形式。 */
const getFileExtension = (filename) => {
  const matched = String(filename || '').match(/\.([a-zA-Z0-9]+)$/)
  return matched ? matched[1].toLowerCase() : ''
}

/** 投递文件可能只给 path，文件名取路径最后一段。 */
const getOriginalFilename = (file) => {
  const name = readString(file?.name)
  if (name) return name

  const path = readString(file?.path)
  if (!path) return ''
  return path.split('/').filter(Boolean).pop() || ''
}

/**
 * 从 DeliverArtifacts 入参中提取待展示的文件信息，供后续的 artifact_delivered 使用。
 * @param {Object} event 工具调用事件
 * @returns {Array<Object>} 文件信息列表
 */
const extractDeliveredFiles = (event) => {
  const files = Array.isArray(event?.input?.files) ? event.input.files : []
  const processedAt = readProcessedAt(event)

  return files
    .map((file) => {
      const originalFilename = getOriginalFilename(file)
      return markJsonArtifact({
        contentType:
          readField(file, 'contentType', 'content_type') ||
          ARTIFACT_CONTENT_TYPE_BY_EXTENSION[getFileExtension(originalFilename)] ||
          '',
        originalFilename,
        // 回退路径拿不到服务端文件 id，卡片点击后不会拉取文件详情。
        fileId: '',
        processedAt
      })
    })
    .filter((file) => file.originalFilename)
}

/**
 * 取投递事件要展示的文件信息。
 * 新版本事件自带完整文件字段，早期版本只有投递确认，需要回退到上一次投递工具的入参。
 * @param {Object} event artifact_delivered 事件
 * @param {Array<Object>} deliveredFiles 最近一次 DeliverArtifacts 解析出的文件
 * @returns {Array<Object>} 文件信息列表，字段与 SSE 写入的一致
 */
const resolveArtifactFiles = (event, deliveredFiles = []) => {
  const originalFilename =
    readField(event, 'originalFilename', 'original_filename') || getOriginalFilename(event)
  if (!originalFilename) return deliveredFiles

  return [
    markJsonArtifact({
      contentType: readField(event, 'contentType', 'content_type') || '',
      fileId: readField(event, 'fileId', 'file_id') || '',
      originalFilename,
      processedAt: readProcessedAt(event)
    })
  ]
}

/** 构造一条 qoder 消息，结构与 SSE 写入 messageList 的对象保持一致。 */
const createQoderItem = ({ id, blockType, extra = {} }) => ({
  id,
  type: 'qoder',
  blockType,
  text: '',
  // 历史事件都是已完成内容，不再展示流式状态。
  streaming: false,
  ...extra
})

/**
 * 提取用户消息的附件，字段与上传组件（UploadedFileCard）读取的一致。
 * attachIndex 是文件地址，UploadedFileCard 用它判断上传是否完成。
 * @param {Object} event 用户消息事件
 * @returns {Array<Object>} 附件列表
 */
const extractUserAttachments = (event) => {
  const attachments = Array.isArray(event?.attachments) ? event.attachments : []

  return attachments
    .filter((file) => file && typeof file === 'object')
    .map((file) => {
      const filename =
        readString(file.filename) || readString(file.mountPath).split('/').filter(Boolean).pop()
      return {
        attachIndex: readString(file.attachIndex),
        filename,
        size: Number(file.size) || 0,
        ext: readString(file.ext) || getFileExtension(filename),
        fileId: readString(file.fileId)
      }
    })
    .filter((file) => file.filename)
}

/** 构造一条用户消息，结构与页面发送时 push 的对象保持一致。 */
const createUserItem = ({
  id,
  content,
  attachments = [],
  processedAt = '',
  businessData = {}
}) => ({
  id,
  type: 'user',
  processedAt,
  message: {
    content,
    businessData,
    referenceFileUrlList: attachments
  }
})

/**
 * 构造一条带文本的 qoder 消息，文本为空时不产生消息，避免历史里出现空气泡。
 * @param {Object} options 事件 id、块类型、文本与附加字段
 * @returns {Array<Object>} 页面消息列表
 */
const createQoderTextItem = ({ id, blockType, text, extra = {} }) =>
  text ? [createQoderItem({ id, blockType, extra: { ...extra, text } })] : []

/**
 * 把单个历史事件转换为一到多条页面消息。
 * @param {Object} event 历史事件
 * @param {Array<Object>} deliveredFiles 最近一次投递的文件信息
 * @returns {Array<Object>} 页面消息列表
 */
const formatQoderEvent = (event, deliveredFiles = []) => {
  const id = event?.id || ''

  switch (getEventType(event)) {
    case QODER_HISTORY_USER_EVENT: {
      const content = extractText(event)
      return content
        ? [
            createUserItem({
              id,
              content,
              attachments: extractUserAttachments(event),
              processedAt: readField(event, 'processedAt', 'processed_at') || '',
              businessData: event?.businessData || {}
            })
          ]
        : []
    }
    case QODER_THINKING_EVENT:
      return createQoderTextItem({
        id,
        blockType: QODER_BLOCK_TYPES.THINKING,
        text: THINKING_TEXT
      })
    case QODER_HISTORY_MESSAGE_EVENT:
      return createQoderTextItem({
        id,
        blockType: QODER_BLOCK_TYPES.MESSAGE,
        text: extractText(event)
      })
    case QODER_TOOL_USE_EVENT:
      return createQoderTextItem({
        id,
        blockType: QODER_BLOCK_TYPES.TOOL_USE,
        text: formatToolUseText(event),
        extra: { toolName: event?.name || '' }
      })
    case QODER_ARTIFACT_DELIVERED_EVENT:
      return resolveArtifactFiles(event, deliveredFiles).map((file, index) =>
        createQoderItem({
          id: index ? `${id}-${index}` : id,
          blockType: QODER_BLOCK_TYPES.ARTIFACT,
          extra: file
        })
      )
    case QODER_ERROR_EVENT:
      return createQoderTextItem({
        id,
        blockType: QODER_BLOCK_TYPES.ERROR,
        text: event?.error?.message || DEFAULT_ERROR_TEXT
      })
    default:
      return []
  }
}

/**
 * 把会话历史事件列表（getSessionEventList 的返回）转换为页面可直接渲染的消息列表。
 * 每个事件单独成条，由 qoder-message 按 blockType 选择渲染分支。
 * @param {Object|Array} res 接口返回，兼容直接传入事件数组
 * @returns {Array<Object>} 页面消息列表
 */
export const formatSessionHistoryMessages = (res) => {
  // 兼容 { result: { data } }、{ data } 与直接返回事件数组三种结构。
  const eventList = [res?.result?.data, res?.data, res].find(Array.isArray) || []

  const messages = []
  // 记录最近一次投递的文件，artifact_delivered 事件本身不带这些信息。
  let deliveredFiles = []

  eventList.forEach((event) => {
    messages.push(
      ...formatQoderEvent(event, deliveredFiles).map((message) =>
        message.type === 'qoder'
          ? { ...message, eventProcessedAt: readField(event, 'processedAt', 'processed_at') || '' }
          : message
      )
    )

    if (isDeliverArtifactsEvent(event)) deliveredFiles = extractDeliveredFiles(event)
  })

  return messages
}
